import React, { FC, useEffect, useState } from 'react';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';

import AvailabilityRepository from 'src/api/repositories/ess/v1/availability.repository';
import { Drawer } from 'src/shared/components';
import { ComponentWithFade } from 'src/shared/hocs';
import { useTranslate } from 'src/shared/providers';

import { EmployeeAvailabilityFutureCyclesSection } from './EmployeeAvailabilityFutureCyclesSection';
import { EmployeeAvailabilityRecurringCycleSection } from './EmployeeAvailabilityRecurringCycleSection';
import { EmployeeAvailabilitySingleDaySection } from './EmployeeAvailabilitySingleDaySection';
import { useEmployeeAvailabilityAddCycle } from './providers/EmployeeAvailabilityAddCycleProvider';
import { EmployeeAvailabilityFutureCyclesProvider } from './providers/EmployeeAvailabilityFutureCyclesProvider';
import { EmployeeAvailabilityRecurringCycleProvider } from './providers/EmployeeAvailabilityRecurringCycleProvider';
import { EmployeeAvailabilitySingleDayProvider } from './providers/EmployeeAvailabilitySingleDayProvider';

enum AvailabilityTabEnums {
  SINGLE_DAY,
  RECURRING_CYCLE,
  FUTURE_CYCLES,
}

interface Props {
  open: boolean;
  onClose: () => void;
  defaultDate?: string;
}

export const EmployeeAvailabilityDrawer: FC<Props> = ({ open, onClose, defaultDate }) => {
  const { t } = useTranslate();
  const [currentTab, setCurrentTab] = useState<AvailabilityTabEnums>(AvailabilityTabEnums.SINGLE_DAY);

  const { isAddMode: isAddCycle, onCancel: handleAddCycleCancel } = useEmployeeAvailabilityAddCycle();

  const {
    data: futureCycles,
    isLoading: isFutureCyclesLoading,
    isFetching: isFutureCyclesFetching,
  } = AvailabilityRepository.endpoints.useGetFutureCyclesQuery();

  const loadingFutureCycles = isFutureCyclesLoading || isFutureCyclesFetching;

  // If there are no future cycles and isAddCycle is false, change tab to single day
  useEffect(() => {
    if (
      loadingFutureCycles ||
      futureCycles?.length ||
      currentTab !== AvailabilityTabEnums.FUTURE_CYCLES ||
      isAddCycle
    ) {
      return;
    }

    setCurrentTab(AvailabilityTabEnums.SINGLE_DAY);
  }, [loadingFutureCycles, futureCycles, currentTab, isAddCycle]);

  // Cancel add cycle on tab change or drawer close
  useEffect(() => {
    if (currentTab === AvailabilityTabEnums.FUTURE_CYCLES && open) {
      return;
    }

    handleAddCycleCancel();
  }, [currentTab, open]);

  // Move tab to future cycles if isAddCycle is true
  useEffect(() => {
    if (!isAddCycle) {
      return;
    }

    setCurrentTab(AvailabilityTabEnums.FUTURE_CYCLES);
  }, [isAddCycle]);

  return (
    <Drawer title={t('availability.specifyAvailability')} overflow={false} open={open} onClose={onClose}>
      <Tabs
        value={currentTab}
        onChange={(_, newTab) => setCurrentTab(newTab)}
        sx={{ mb: 4, '&, button': { height: '38px', minHeight: '38px' } }}
      >
        <Tab
          value={AvailabilityTabEnums.SINGLE_DAY}
          id="single-day-tab"
          label={t('availability.singleDay')}
          sx={{ textTransform: 'none' }}
        />
        <Tab
          value={AvailabilityTabEnums.RECURRING_CYCLE}
          id="recurring-cycle-tab"
          label={t('availability.recurringCycle')}
          sx={{ textTransform: 'none' }}
        />
        {(isAddCycle || !!futureCycles?.length || loadingFutureCycles) && (
          <Tab
            value={AvailabilityTabEnums.FUTURE_CYCLES}
            id="future-cycles-tab"
            label={t('availability.futureCycles')}
            sx={{ textTransform: 'none' }}
          />
        )}
      </Tabs>

      <Stack direction="column" height="calc(100% - 80px)" overflow="auto">
        <EmployeeAvailabilitySingleDayProvider defaultDate={defaultDate}>
          <ComponentWithFade show={currentTab === AvailabilityTabEnums.SINGLE_DAY}>
            <EmployeeAvailabilitySingleDaySection />
          </ComponentWithFade>
        </EmployeeAvailabilitySingleDayProvider>

        <EmployeeAvailabilityRecurringCycleProvider>
          <ComponentWithFade show={currentTab === AvailabilityTabEnums.RECURRING_CYCLE}>
            <EmployeeAvailabilityRecurringCycleSection />
          </ComponentWithFade>
        </EmployeeAvailabilityRecurringCycleProvider>

        <EmployeeAvailabilityFutureCyclesProvider futureCycles={futureCycles} loading={loadingFutureCycles}>
          <ComponentWithFade show={currentTab === AvailabilityTabEnums.FUTURE_CYCLES}>
            <EmployeeAvailabilityFutureCyclesSection />
          </ComponentWithFade>
        </EmployeeAvailabilityFutureCyclesProvider>
      </Stack>
    </Drawer>
  );
};
